// src\components\Video\VideoPlayer.js
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const VideoPlayer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [particles, setParticles] = useState([]);
  const cardRef = useRef(null);
  const videoRef = useRef(null);

  // 检测屏幕宽度
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 获取从列表页传递的视频数据
  const videoData = location.state?.video;
  
  // 当前视频数据
  const currentVideo = videoData || {
    id: 1,
    title: '非遗故事——鸟哨',
    description: '本片记录了国家级非物质文化遗产"鸟哨"的传承故事。通过采访老艺人和年轻学徒，展现了这项古老技艺在当代的生存状态与创新尝试。',
    host: '声声',
    uploadDate: '2024-03-15',
    views: '1.2万',
    likes: 896,
    rating: 4.8,
    thumbnail: 'https://picsum.photos/id/20/800/450',
    videoUrl: 'https://www.w3schools.com/html/mov_bbb.mp4',
    category: '传统',
    resolution: 'HD'
  };

  // 鼠标移动追踪（液态玻璃效果）
  const handleMouseMove = (e) => {
    if (cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      setMousePosition({
        x: ((e.clientX - rect.left) / rect.width) * 100,
        y: ((e.clientY - rect.top) / rect.height) * 100
      });
    }
  };

  // 点赞粒子效果
  const handleLike = () => {
    if (isLiked) {
      setLikesCount(prev => prev - 1);
      setIsLiked(false);
    } else {
      setLikesCount(prev => prev + 1);
      setIsLiked(true);
      // 创建粒子效果
      const newParticles = Array.from({ length: 12 }, (_, i) => ({
        id: Date.now() + i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        angle: Math.random() * Math.PI * 2,
        speed: 2 + Math.random() * 3,
        size: 4 + Math.random() * 8,
        life: 1
      }));
      setParticles(newParticles);
      
      setTimeout(() => setParticles([]), 2000);
    }
  };

  // 粒子动画效果
  useEffect(() => {
    if (particles.length > 0) {
      const interval = setInterval(() => {
        setParticles(prev => prev.map(p => ({
          ...p,
          x: p.x + Math.cos(p.angle) * p.speed,
          y: p.y + Math.sin(p.angle) * p.speed,
          life: p.life - 0.02
        })).filter(p => p.life > 0));
      }, 16);
      return () => clearInterval(interval);
    }
  }, [particles]);

  const handleFavorite = () => {
    setIsFavorited(!isFavorited);
  };

  const handleShare = async () => {
    const shareData = {
      title: currentVideo.title,
      text: currentVideo.description,
      url: window.location.href,
    };
    
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log('分享取消');
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('链接已复制到剪贴板');
    }
  };

  // 推荐视频数据
  const recommendedVideos = [
    { 
      id: 2, 
      title: '为什么天空是蓝色的', 
      host: '立达', 
      duration: '08:45', 
      views: '2.5万',
      rating: 4.6,
      thumbnail: 'https://picsum.photos/id/21/160/90',
      category: '科普'
    },
    { 
      id: 3, 
      title: '穿越亿年，探寻恐龙的神秘世界', 
      host: '王青', 
      duration: '15:54', 
      views: '3.8万',
      rating: 4.9,
      thumbnail: 'https://picsum.photos/id/22/160/90',
      category: '科普'
    },
    { 
      id: 4, 
      title: '天下第一行书《兰亭集序》', 
      host: '卢林平', 
      duration: '08:03', 
      views: '1.8万',
      rating: 4.7,
      thumbnail: 'https://picsum.photos/id/23/160/90',
      category: '艺术'
    },
    { 
      id: 5, 
      title: '非遗——白蛇传', 
      host: '声声', 
      duration: '01:41', 
      views: '3.2万',
      rating: 4.8,
      thumbnail: 'https://picsum.photos/id/24/160/90',
      category: '传统'
    },
    { 
      id: 6, 
      title: '发生山火时，我们该如何自救？', 
      host: '丁忆', 
      duration: '05:24', 
      views: '4.1万',
      rating: 4.5,
      thumbnail: 'https://picsum.photos/id/25/160/90',
      category: '科普'
    },
  ];

  const handleRecommendedClick = (video) => {
    window.location.href = `/video/${video.id}`;
  };

  return (
    <div style={styles.videoPlayerPage}>
      {/* 粒子效果层 */}
      {particles.map(particle => (
        <div
          key={particle.id}
          style={{
            ...styles.particle,
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            opacity: particle.life,
            transform: `scale(${particle.life})`
          }}
        />
      ))}

      {/* 返回按钮 - 磁性效果 */}
      <button 
        style={{
          ...styles.backBtn,
          ...(isHovering ? styles.backBtnMagnetic : {})
        }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        onClick={() => navigate(-1)}
      >
        ← 返回列表
      </button>

      <div style={{
        ...styles.playerContainer,
        ...(isMobile ? styles.playerContainerMobile : {}),
      }}>
        {/* 左侧：主播放器 */}
        <div style={styles.mainPlayerWrapper}>
          <div 
            ref={cardRef}
            style={styles.mainPlayer}
            onMouseMove={handleMouseMove}
          >
            {/* 液态玻璃效果层 - 只在视频播放器上 */}
            <div style={{
              ...styles.liquidGlass,
              background: `radial-gradient(circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 100%)`
            }} />
            
            <video 
              ref={videoRef}
              controls 
              src={currentVideo.videoUrl}
              poster={currentVideo.thumbnail}
              style={styles.videoElement}
              controlsList="nodownload"
            >
              您的浏览器不支持 video 标签。
            </video>
            
            <div style={styles.videoInfo}>
              <h1 style={styles.videoTitle}>{currentVideo.title}</h1>
              
              <div style={styles.videoMeta}>
                <div style={styles.metaLeft}>
                  <span>🎙️ {currentVideo.host}</span>
                  <span>📅 {currentVideo.uploadDate}</span>
                  <span>👁️ {currentVideo.views}</span>
                  <span style={styles.categoryBadge}>{currentVideo.category}</span>
                </div>
                <div style={styles.metaRight}>
                  <span>⭐ {currentVideo.rating}</span>
                </div>
              </div>
              
              {/* 磁性按钮组 */}
              <div style={styles.actionButtons}>
                <button 
                  style={{
                    ...styles.actionBtn,
                    ...(isLiked ? styles.actionBtnActive : {})
                  }}
                  onClick={handleLike}
                >
                  {isLiked ? '❤️' : '👍'} {likesCount}
                </button>
                <button 
                  style={{
                    ...styles.actionBtn,
                    ...(isFavorited ? styles.actionBtnActive : {})
                  }}
                  onClick={handleFavorite}
                >
                  {isFavorited ? '⭐' : '☆'} 收藏
                </button>
                <button 
                  style={styles.actionBtn}
                  onClick={handleShare}
                >
                  ↗ 分享
                </button>
              </div>
              
              <div style={styles.descriptionSection}>
                <h3 style={styles.descriptionTitle}>简介</h3>
                <p style={styles.videoDescription}>{currentVideo.description}</p>
              </div>
            </div>
          </div>
        </div>

        {/* 右侧：推荐列表 - 贴近背景 */}
        <aside style={styles.recommendations}>
          <h3 style={styles.recTitle}>相关推荐</h3>
          <div style={styles.recList}>
            {recommendedVideos.map(video => (
              <div 
                key={video.id} 
                style={styles.recItem}
                onClick={() => handleRecommendedClick(video)}
              >
                <div style={styles.recThumbWrapper}>
                  <img 
                    src={video.thumbnail} 
                    alt={video.title} 
                    style={styles.recThumb}
                    onError={(e) => {
                      e.target.src = 'https://picsum.photos/id/1/160/90';
                    }}
                  />
                </div>
                <div style={styles.recInfo}>
                  <h4 style={styles.recTitleText}>{video.title}</h4>
                  <p style={styles.recHost}>{video.host}</p>
                  <div style={styles.recMeta}>
                    <span>{video.duration}</span>
                    <span>👁️ {video.views}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
};

const styles = {
  videoPlayerPage: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    backgroundColor: '#f8f9fa', // 简化为干净的中性背景
    minHeight: '100vh',
    padding: '20px',
    position: 'relative',
    overflowX: 'hidden',
  },
  
  // 粒子效果
  particle: {
    position: 'fixed',
    borderRadius: '50%',
    background: 'radial-gradient(circle, #ff6b6b, #ff8787)',
    pointerEvents: 'none',
    zIndex: 1000,
    transition: 'all 0.1s linear',
  },
  
  // 磁性返回按钮 - 透明风格
  backBtn: {
    background: 'transparent',
    border: 'none',
    color: '#666',
    fontSize: '15px',
    cursor: 'pointer',
    marginBottom: '20px',
    padding: '8px 16px',
    borderRadius: '20px',
    transition: 'all 0.3s cubic-bezier(0.34, 1.2, 0.64, 1)',
    position: 'relative',
    zIndex: 10,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  backBtnMagnetic: {
    transform: 'translateX(-4px)',
    color: '#007bff',
    background: 'rgba(0,123,255,0.05)',
  },
  
  playerContainer: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) 360px',
    gap: '24px',
    maxWidth: '1400px',
    margin: '0 auto',
    position: 'relative',
    zIndex: 5,
  },
  playerContainerMobile: {
    gridTemplateColumns: '1fr',
    gap: '20px',
  },
  
  mainPlayerWrapper: {
    minWidth: 0, // 防止溢出
  },
  
  // 主播放器 - 无外框，内容直接展示
  mainPlayer: {
    background: 'transparent',
    borderRadius: '0',
    overflow: 'visible',
    position: 'relative',
  },
  liquidGlass: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: 'none',
    zIndex: 1,
    transition: 'background 0.1s ease',
    borderRadius: '12px',
  },
  videoElement: {
    width: '100%',
    display: 'block',
    backgroundColor: '#000',
    borderRadius: '12px',
    position: 'relative',
    zIndex: 2,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  videoInfo: {
    padding: '16px 4px 24px 4px',
    position: 'relative',
    zIndex: 2,
  },
  videoTitle: {
    fontSize: '20px',
    margin: '0 0 12px 0',
    color: '#1f1f1f',
    lineHeight: '1.3',
    fontWeight: '600',
  },
  videoMeta: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: '16px',
    paddingBottom: '12px',
    borderBottom: '1px solid #e5e5e5',
  },
  metaLeft: {
    display: 'flex',
    gap: '16px',
    color: '#606060',
    fontSize: '14px',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  categoryBadge: {
    background: '#f0f0f0',
    padding: '2px 10px',
    borderRadius: '12px',
    color: '#606060',
    fontSize: '12px',
  },
  metaRight: {
    fontSize: '14px',
    color: '#ff9800',
    fontWeight: '500',
  },
  
  // 磁性按钮组 - YouTube风格
  actionButtons: {
    display: 'flex',
    gap: '12px',
    marginBottom: '20px',
    flexWrap: 'wrap',
  },
  actionBtn: {
    background: '#f0f0f0',
    border: 'none',
    padding: '8px 20px',
    borderRadius: '24px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontSize: '14px',
    color: '#1f1f1f',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    ':hover': {
      background: '#e5e5e5',
      transform: 'scale(1.02)',
    },
  },
  actionBtnActive: {
    background: '#ffebee',
    color: '#e91e63',
  },
  
  descriptionSection: {
    marginTop: '16px',
  },
  descriptionTitle: {
    fontSize: '16px',
    margin: '0 0 10px 0',
    color: '#1f1f1f',
    fontWeight: '500',
  },
  videoDescription: {
    lineHeight: '1.6',
    color: '#606060',
    whiteSpace: 'pre-line',
    fontSize: '14px',
  },
  
  // 推荐列表 - 无外框，贴近背景
  recommendations: {
    background: 'transparent',
    borderRadius: '0',
    padding: '0',
    height: 'fit-content',
    position: 'sticky',
    top: '20px',
  },
  recTitle: {
    fontSize: '16px',
    margin: '0 0 16px 0',
    color: '#1f1f1f',
    fontWeight: '500',
  },
  recList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  recItem: {
    display: 'flex',
    gap: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    padding: '8px',
    borderRadius: '8px',
    ':hover': {
      background: '#f0f0f0',
    },
  },
  recThumbWrapper: {
    flexShrink: 0,
  },
  recThumb: {
    width: '168px',
    height: '94px',
    objectFit: 'cover',
    borderRadius: '8px',
  },
  recInfo: {
    flex: 1,
    minWidth: 0,
  },
  recTitleText: {
    fontSize: '14px',
    margin: '0 0 4px 0',
    color: '#1f1f1f',
    lineHeight: '1.4',
    fontWeight: '500',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  recHost: {
    fontSize: '12px',
    color: '#606060',
    margin: '0 0 4px 0',
  },
  recMeta: {
    display: 'flex',
    gap: '8px',
    fontSize: '12px',
    color: '#909090',
  },
};

export default VideoPlayer;